import { useEffect, useState } from 'react'

interface PerformanceMetrics {
    drawCalls: number
    fps: number
}

export function usePerformanceMetrics(): PerformanceMetrics {
    const [drawCalls, setDrawCalls] = useState(0)
    const [fps, setFps] = useState(0)

    useEffect(() => {
        const canvas = document.querySelector<HTMLCanvasElement>('canvas')

        if (!canvas) return

        const rawGl =
            (canvas.getContext('webgl2') as WebGL2RenderingContext | null) ??
            (canvas.getContext('webgl') as WebGLRenderingContext | null)

        if (!rawGl) return

        type DrawElsArgs = Parameters<WebGLRenderingContext['drawElements']>
        type DrawArrArgs = Parameters<WebGLRenderingContext['drawArrays']>

        let tempDrawCalls = 0
        let fpsValues: number[] = []
        let lastTime = performance.now()
        let rafId: number

        // Hijack WebGL
        const gl = rawGl as WebGLRenderingContext | WebGL2RenderingContext
        const realDrawElements = gl.drawElements.bind(gl)
        const realDrawArrays = gl.drawArrays.bind(gl)

        gl.drawElements = (...args: DrawElsArgs) => {
            tempDrawCalls += 1

            return realDrawElements(...args)
        }
        gl.drawArrays = (...args: DrawArrArgs) => {
            tempDrawCalls += 1

            return realDrawArrays(...args)
        }

        const tick = (now: number) => {
            // FPS
            const delta = now - lastTime

            lastTime = now
            fpsValues.push(1000 / delta)

            if (fpsValues.length >= 30) {
                const avg = fpsValues.reduce((a, b) => a + b, 0) / fpsValues.length

                setFps(avg)
                fpsValues = []
            }

            // Draw calls
            setDrawCalls(tempDrawCalls)
            tempDrawCalls = 0

            rafId = requestAnimationFrame(tick)
        }

        rafId = requestAnimationFrame(tick)

        return () => {
            cancelAnimationFrame(rafId)

            gl.drawElements = realDrawElements
            gl.drawArrays = realDrawArrays
        }
    }, [])

    return { drawCalls, fps }
}

export default usePerformanceMetrics
